import { useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { toast } from "sonner";
import { Building2, ImagePlus, X, Palette, EyeOff, Sparkles, ArrowUpRight } from "lucide-react";
import { UpgradeModal } from "@/components/upgrade/UpgradeModal";
import ProposalReferenceUpload from "./ProposalReferenceUpload";
import type { ProposalReference } from "@/lib/proposalReferences";

export interface ClientInfo {
  companyName: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  logoUrl: string;
  accentColor: string;
  footerText: string;
  removeBranding: boolean;
  brandingTextPosition?: "header" | "footer" | "both";
  references?: ProposalReference[];
}

type IndustryType = "security" | "cleaning" | "event-staffing" | "private-investigation" | string;

const INDUSTRY_EXAMPLES: Record<string, { company: string; contact: string; email: string; footer: string }> = {
  security: {
    company: "Apex Security Group",
    contact: "James Carter",
    email: "james@apexsecurity.com",
    footer: "Confidential — Prepared by Apex Security Group",
  },
  cleaning: {
    company: "Pristine Janitorial Services",
    contact: "Maria Gonzalez",
    email: "maria@pristinejanitorial.com",
    footer: "Confidential — Prepared by Pristine Janitorial Services",
  },
  "event-staffing": {
    company: "Elite Event Staffing",
    contact: "Sarah Mitchell",
    email: "sarah@elitestaffing.com",
    footer: "Confidential — Prepared by Elite Event Staffing",
  },
  "private-investigation": {
    company: "Vanguard Investigations",
    contact: "Robert Chen",
    email: "robert@vanguardpi.com",
    footer: "Confidential — Prepared by Vanguard Investigations",
  },
  "construction-services": {
    company: "Summit Builders Group",
    contact: "David Torres",
    email: "david@summitbuilders.com",
    footer: "Confidential — Prepared by Summit Builders Group",
  },
};

interface ClientCustomizationProps {
  info: ClientInfo;
  onChange: (info: ClientInfo) => void;
  showLogoUpload?: boolean;
  showWhiteLabel?: boolean;
  industry?: IndustryType;
  headerTitle?: string;
  headerDescription?: string;
}

const ClientCustomization = ({ 
  info, 
  onChange, 
  showLogoUpload = false, 
  showWhiteLabel = false, 
  industry = "security",
  headerTitle = "Your Company Info",
  headerDescription = "This is your company's branding that appears on the proposal."
}: ClientCustomizationProps) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [showWhiteLabelUpgrade, setShowWhiteLabelUpgrade] = useState(false);
  const update = (key: keyof ClientInfo, value: string | boolean) => onChange({ ...info, [key]: value });

  const examples = INDUSTRY_EXAMPLES[industry] || INDUSTRY_EXAMPLES.security;

  const handleLogoFile = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    if (!file.type.startsWith("image/") || file.size > 1024 * 1024) {
      toast.error("Choose an image smaller than 1 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") update("logoUrl", reader.result);
    };
    reader.onerror = () => toast.error("The logo could not be read. Please try again.");
    reader.readAsDataURL(file);
  };

  const fields = [
    { key: "companyName" as const, label: "Your Company Name", placeholder: examples.company },
    { key: "contactName" as const, label: "Your Name", placeholder: examples.contact },
    { key: "contactEmail" as const, label: "Your Email", placeholder: examples.email },
    { key: "contactPhone" as const, label: "Your Phone", placeholder: "(555) 123-4567" },
  ];

  return (
    <Card className="border-border bg-card shadow-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-foreground">
          <Building2 className="h-5 w-5 text-primary" />
          {headerTitle}
        </CardTitle>
        <p className="text-xs text-muted-foreground">{headerDescription}</p>
      </CardHeader>
      <CardContent className="space-y-3">
        {fields.map(({ key, label, placeholder }) => (
          <div key={key}>
            <Label className="text-muted-foreground">{label}</Label>
            <Input
              value={info[key] as string}
              onChange={(e) => update(key, e.target.value)}
              placeholder={placeholder}
              className="mt-1 bg-secondary border-border text-foreground"
            />
          </div>
        ))}

        {showLogoUpload && (
          <div>
            <Label className="text-muted-foreground">Company Logo</Label>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => handleLogoFile(e.target.files)}
            />
            {info.logoUrl ? (
              <div className="mt-1 relative inline-block group">
                <img
                  src={info.logoUrl}
                  alt="Company logo"
                  className="h-16 max-w-[180px] object-contain rounded-lg border border-border bg-secondary p-2"
                />
                <Button variant="destructive" size="icon" aria-label="Remove logo" title="Remove logo"
                  onClick={() => update("logoUrl", "")}
                  className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="h-3 w-3" />
                </Button>
                <Button variant="link" size="sm"
                  onClick={() => fileRef.current?.click()}
                  className="block mt-1 text-xs text-primary hover:underline"
                >
                  Change logo
                </Button>
              </div>
            ) : (
              <Button variant="outline"
                onClick={() => fileRef.current?.click()}
                className="mt-1 w-full h-16 rounded-lg border-2 border-dashed border-border hover:border-primary/50 flex items-center justify-center gap-2 text-muted-foreground hover:text-primary transition-colors"
              >
                <ImagePlus className="h-4 w-4" />
                <span className="text-xs">Upload logo (PNG, JPG, SVG)</span>
              </Button>
            )}
          </div>
        )}

        {showWhiteLabel && (
          <div className="border-t border-border pt-3 space-y-4">
            <div className="flex items-center gap-2">
              <Palette className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-foreground">White-Label Branding</span>
              <Badge className="bg-primary/10 text-primary border-primary/20 text-xs">Enterprise</Badge>
            </div>

            <div className="flex items-start justify-between gap-3 rounded-lg border border-border bg-secondary/50 px-3 py-2.5">
              <div className="flex items-center gap-2">
                <EyeOff className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-foreground">Remove Sentinel Forge Branding</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Hides Sentinel Forge name from the app header and PDFs. Your company name is shown instead.</p>
                </div>
              </div>
              <Switch
                checked={info.removeBranding}
                onCheckedChange={(checked) => update("removeBranding", checked)}
                className="mt-0.5 shrink-0"
              />
            </div>

            <div>
              <Label className="text-muted-foreground">Proposal Accent Color</Label>
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="color"
                  value={info.accentColor || "#1e3a5f"}
                  onChange={(e) => update("accentColor", e.target.value)}
                  className="h-9 w-9 rounded border border-border cursor-pointer bg-secondary p-0.5"
                />
                <Input
                  value={info.accentColor || "#1e3a5f"}
                  onChange={(e) => update("accentColor", e.target.value)}
                  placeholder="#1e3a5f"
                  className="flex-1 bg-secondary border-border text-foreground font-mono text-sm"
                  maxLength={7}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">Used for section headers and accents in the PDF.</p>
            </div>

            <div>
              <Label className="text-muted-foreground">Custom Header / Footer Text</Label>
              <Input
                value={info.footerText}
                onChange={(e) => update("footerText", e.target.value)}
                placeholder={examples.footer}
                className="mt-1 bg-secondary border-border text-foreground"
              />
              <ToggleGroup type="single" value={info.brandingTextPosition ?? "footer"} onValueChange={(value) => {
                if (value === "header" || value === "footer" || value === "both") update("brandingTextPosition", value);
              }} variant="outline" className="mt-3 justify-start flex-wrap" aria-label="Custom text placement">
                <ToggleGroupItem value="header" aria-label="Header">Header</ToggleGroupItem>
                <ToggleGroupItem value="footer" aria-label="Footer">Footer</ToggleGroupItem>
                <ToggleGroupItem value="both" aria-label="Header and footer">Both</ToggleGroupItem>
              </ToggleGroup>
            </div>
          </div>
        )}

        {/* Locked teaser for users who can upload a logo but don't have white-labeling */}
        {showLogoUpload && !showWhiteLabel && (
          <div className="border-t border-border pt-3">
            <div className="rounded-lg border border-dashed border-primary/30 bg-primary/5 p-3 flex items-start justify-between gap-3">
              <div className="flex items-start gap-2">
                <Palette className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-foreground">White-Label Branding</p>
                    <Badge className="bg-primary/10 text-primary border-primary/30 text-[10px] gap-1">
                      <Sparkles className="h-2.5 w-2.5" />
                      Enterprise
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Replace Sentinel Forge branding with your own logo, colors, and footer.
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="shrink-0"
                onClick={() => setShowWhiteLabelUpgrade(true)}
              >
                Unlock
                <ArrowUpRight className="ml-1 h-3 w-3" />
              </Button>
            </div>
          </div>
        )}
        <ProposalReferenceUpload value={info.references ?? []} onChange={(references) => onChange({ ...info, references })} />
      </CardContent>

      <UpgradeModal
        open={showWhiteLabelUpgrade}
        onOpenChange={setShowWhiteLabelUpgrade}
        feature="white-labeling"
      />
    </Card>
  );
};

export default ClientCustomization;
